import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { DocumentModel } from '@data-layer/models/documents';
import { enumCompanyBusinessType, enumCompanyPartnershipType } from '@data-layer/enums/companies';
import {
  controllingPersonHeadingKey,
  controllingPersonTooltipKey,
  isPartnership,
  minControllingPersons,
  requiresControllingPersons,
} from '@constants/business-types';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { createControllingPersonFormGroup } from '@helpers/controlling-person-form.helper';
import { DocumentsService } from '@services/documents.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';

// #13269 — a scan picked before "Save changes"; kept in memory by edit-company and uploaded after the PUT.
// previewUrl is a data: URL (NOT an object URL — Angular's sanitizer blocks blob: in [src]);
// null while the FileReader is still producing it.
export interface ControllingPersonScanBufferEntry {
  file: File;
  previewUrl: string | null;
  isImage: boolean;
}

@Component({
  selector: 'company-controlling-persons-form',
  templateUrl: './company-controlling-persons-form.component.html',
  styleUrls: ['./company-controlling-persons-form.component.scss'],
})
export class CompanyControllingPersonsFormComponent {
  @Input() public formGroup: UntypedFormGroup;
  @Input() public isShowDescription: boolean = false;
  @Input() public personDocuments: DocumentModel[] = [];
  @Input() public scanBuffer: Map<AbstractControl, ControllingPersonScanBufferEntry> = new Map();
  @Input() public scanUrls: Map<number, string> = new Map();
  @Input() public uploadingGroups: Set<AbstractControl> = new Set();
  @Input() public scanErrors: Map<AbstractControl, string> = new Map();

  @Output() public scanSelected$: EventEmitter<{ group: UntypedFormGroup; file: File }> = new EventEmitter<{
    group: UntypedFormGroup;
    file: File;
  }>();
  @Output() public personRemoved$: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();
  @Output() public retryScanUpload$: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();

  public CUSTOM_MASK: any = CUSTOM_MASK;
  public nameErrors: IErrorForm = { required: 'FORM_ERROR.REQUIRED' };
  public ssnErrors: IErrorForm = { required: 'FORM_ERROR.REQUIRED', notMatchSSN: 'FORM_ERROR.NOT_MATCH_SSN' };
  // #13281 — requiredTrue on the citizenship checkbox surfaces as the same message as the MI "I declare" checkbox
  public citizenErrors: IErrorForm = { required: 'FORM_ERROR.CONFIRM_STATEMENT' };
  public isRemoveConfirmOpen: boolean = false;
  private pendingRemoveIndex: number = null;

  constructor(private documentsService: DocumentsService) {}

  public get personsArray(): UntypedFormArray | null {
    return this.formGroup.get('controllingPersons') as UntypedFormArray | null;
  }

  public get businessType(): enumCompanyBusinessType {
    return this.formGroup.get('businessType')?.value;
  }

  private get partnershipType(): enumCompanyPartnershipType {
    return this.formGroup.get('partnershipType')?.value;
  }

  // Control-exists guard protects placements whose form definitions omit the array (ES/ISF/LCL/bonds/CBP sidebars)
  public get isVisible(): boolean {
    return !!this.personsArray && requiresControllingPersons(this.businessType);
  }

  public get minSections(): number {
    return minControllingPersons(this.businessType);
  }

  public get headingKey(): string {
    return controllingPersonHeadingKey(this.businessType, this.partnershipType);
  }

  public get headerTooltipKey(): string | null {
    return controllingPersonTooltipKey(this.businessType, this.partnershipType);
  }

  public get infoPanelKey(): string {
    return isPartnership(this.businessType)
      ? 'COMPANY.CONTROLLING_PERSONS.INFO_PARTNER'
      : 'COMPANY.CONTROLLING_PERSONS.INFO_BENEFICIAL_OWNER';
  }

  public get addButtonKey(): string {
    return isPartnership(this.businessType)
      ? 'COMPANY.CONTROLLING_PERSONS.ADD_PARTNER'
      : 'COMPANY.CONTROLLING_PERSONS.ADD_BENEFICIAL_OWNER';
  }

  public trackByGroup(_index: number, group: AbstractControl): AbstractControl {
    return group;
  }

  public personGroupAt(index: number): UntypedFormGroup {
    return this.personsArray.at(index) as UntypedFormGroup;
  }

  // The trash icon exists only above the required minimum: from #2 for Corporation/LLC, from #3 for Partnership
  public showRemove(index: number): boolean {
    return index >= this.minSections;
  }

  public addPerson(): void {
    this.personsArray.push(createControllingPersonFormGroup());
    this.personsArray.markAsDirty();
  }

  public requestRemove(index: number): void {
    if (this.personHasData(this.personGroupAt(index))) {
      this.pendingRemoveIndex = index;
      this.isRemoveConfirmOpen = true;
      return;
    }
    this.removePerson(index);
  }

  public confirmRemove(): void {
    if (this.pendingRemoveIndex !== null) {
      this.removePerson(this.pendingRemoveIndex);
    }
    this.cancelRemove();
  }

  public cancelRemove(): void {
    this.pendingRemoveIndex = null;
    this.isRemoveConfirmOpen = false;
  }

  public onUploadScan(group: UntypedFormGroup, file: File): void {
    this.scanSelected$.emit({ group, file });
  }

  public onRetryScan(group: UntypedFormGroup): void {
    this.retryScanUpload$.emit(group);
  }

  public isScanLoading(group: UntypedFormGroup): boolean {
    return this.uploadingGroups.has(group);
  }

  public scanErrorKey(group: UntypedFormGroup): string | null {
    return this.scanErrors.get(group) ?? null;
  }

  // Buffered file first (pre-save preview), then the persisted scan of the saved person
  public scanDownloadLink(group: UntypedFormGroup): string | null {
    const buffered: ControllingPersonScanBufferEntry = this.scanBuffer.get(group);
    if (buffered) {
      return buffered.previewUrl;
    }
    const document: DocumentModel = this.scanDocument(group);
    if (!document) {
      return null;
    }
    // A non-http marker keeps the "uploaded" layout while the download url is still being fetched
    return this.scanUrls.get(document.id) ?? 'uploaded-document';
  }

  public scanFileName(group: UntypedFormGroup): string {
    const buffered: ControllingPersonScanBufferEntry = this.scanBuffer.get(group);
    if (buffered) {
      return buffered.file.name;
    }
    return this.scanDocument(group)?.fileName ?? '';
  }

  public isScanNonImage(group: UntypedFormGroup): boolean {
    const buffered: ControllingPersonScanBufferEntry = this.scanBuffer.get(group);
    if (buffered) {
      return !buffered.isImage;
    }
    const document: DocumentModel = this.scanDocument(group);
    if (!document) {
      return false;
    }
    return !this.scanUrls.has(document.id) || !this.documentsService.checkImageFileExtension(document.fileName);
  }

  private scanDocument(group: UntypedFormGroup): DocumentModel | null {
    const personId: number = group.get('id')?.value;
    if (!personId) {
      return null;
    }
    return (this.personDocuments || []).find((document: DocumentModel) => document.relatedEntityId === personId) ?? null;
  }

  private personHasData(group: UntypedFormGroup): boolean {
    const hasFieldData: boolean =
      !!group.get('firstName')?.value ||
      !!group.get('lastName')?.value ||
      !!group.get('socialSecurityNumber')?.value ||
      !!group.get('isUsCitizenOrResident')?.value;
    return hasFieldData || this.scanBuffer.has(group) || !!this.scanDocument(group);
  }

  private removePerson(index: number): void {
    const group: UntypedFormGroup = this.personGroupAt(index);
    this.personsArray.removeAt(index);
    this.personsArray.markAsDirty();
    // Deletion reaches the server only via the next PUT (the person id is simply absent from the array)
    this.personRemoved$.emit(group);
  }
}
